import React, { useMemo, useState } from "react";
import useRouter from "@stadium/hooks/lib/use-custom-route";
import { useSelector } from "react-redux";

import { InternalButton } from "@components/shared/buttons";
import Multimedia from "@sharedComponents/multimedia";
import CreateShopCancelSavedChanges from "@components/modals/cancel-saved-changes";
import AuthLogin from "@components/auth/buttons/login";

import { useJuneSoEventTracking } from "@stadium/services/june-analytics/hooks/use-june-so-event-tracking";
import { useCreateShopContext } from "@contexts/create-shop-context";
import { externalRoutes } from "@constants/external-routes";
import { useAuth0V2 } from "@stadium/services/auth0";
import { getPublishStoreRoute } from "@constants/routes";
import useUpdateDummyStore from "@hooks/store/use-update-dummy-store";
import { useGuideTourContext } from "@contexts/guide-tour";
import { useWorkspaceContext } from "@contexts/workspace-context";
import { useStoreContext } from "@components/store/provider";

import { getMenu } from "@selectors/menu";
import { getAllMenuTaxons } from "@helpers/menu";
import { getIsPricelessShop } from "@selectors/store";

import { PRICELESS_SHOP, SHOP_CREATION, SHOP_CREATION_FLOW } from "@constants/june-so";

import stadiumLogo from "@stadium/ui/src/assets/images/svg/footer/stadium-logo.svg";
import styles from "./main.module.scss";

const CreateShopBanner = () => {
  const { isAuthenticated } = useAuth0V2();
  const { getDashboardUrlWithCurrentWorkspaceSlug } = useWorkspaceContext();
  const menu = useSelector(getMenu);
  const isPricelessShop = useSelector(getIsPricelessShop);

  const { trackEvent } = useJuneSoEventTracking({ services: SHOP_CREATION_FLOW });

  const { allChildren: menuItems } = menu;

  const { allMenuTaxonIds: taxonIds } = useMemo(() => getAllMenuTaxons(menuItems), [menuItems]);

  const { push, query } = useRouter();
  const { countryCode, lang, storeCode, storeNumber, isNewRoute } = useStoreContext();

  const { category } = query;
  const { clearDummyStoreParams, dummyStoreNumber, dummyStoreReturnUrl } = useCreateShopContext();
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);

  const { startGuideTour, guideTourSteps } = useGuideTourContext();

  const { step5 = {} } = guideTourSteps;

  const closeUnsavedChangesModalModal = () => setIsUnsavedChangesModalOpen(false);

  const { loading: isPublishingStore, publishDummyStore } = useUpdateDummyStore(dummyStoreNumber);

  const loginOptions = {
    appState: {
      returnTo: getPublishStoreRoute({
        countryCode,
        lang,
        storeCode,
        storeNumber,
        dummyStoreNumber,
        isNewRoute,
      }),
    },
    signUp: true,
  };

  const handleExit = () => {
    if (isAuthenticated) push(getDashboardUrlWithCurrentWorkspaceSlug(externalRoutes.dashboard()));
    else
      push(
        dummyStoreReturnUrl ||
          getDashboardUrlWithCurrentWorkspaceSlug(externalRoutes.chooseTemplates()),
      );
    clearDummyStoreParams();
  };

  const renderSaveButton = (onClick = () => {}) => (
    <InternalButton
      styleType="primary-outline"
      onClick={onClick}
      className={styles.btnPublish}
      isLoading={isPublishingStore}
      ref={step5.ref}
    >
      Save&nbsp;<span className={styles.hideMobile}> & Publish</span>
    </InternalButton>
  );

  const renderPublishCta = () => {
    if (!isAuthenticated)
      return <AuthLogin loginOptions={loginOptions}>{renderSaveButton()}</AuthLogin>;

    return renderSaveButton(() => {
      trackEvent({
        componentName: isPricelessShop ? PRICELESS_SHOP : SHOP_CREATION,
        elementPosition: "menu",
        target: "save-and-publish",
      });
      publishDummyStore({
        successCb: clearDummyStoreParams,
        data: { taxonIds },
      });
    });
  };

  const renderGuideTourCta = () =>
    !category && (
      <InternalButton
        styleType="primary-outline"
        onClick={startGuideTour}
        className={styles.btnTour}
        title="Guided Tour"
      >
        <span className={styles.tourIcon} />
      </InternalButton>
    );

  return (
    <div
      className={`create-shop-banner-header ${styles.createShopBannerHeader} ${
        isUnsavedChangesModalOpen ? styles["openModal"] : ""
      }`}
    >
      <div className={styles.headerContainer}>
        <InternalButton
          styleType="link"
          onClick={() => setIsUnsavedChangesModalOpen(true)}
          className={styles.exitLink}
        >
          <span className={styles.hideMobile}>Exit</span>
        </InternalButton>

        <Multimedia
          type="image"
          width={132}
          height={32}
          src={stadiumLogo}
          className={styles.headerLogo}
        />

        <div className={styles.rightBlock}>
          {renderGuideTourCta()}
          {renderPublishCta()}
          {isUnsavedChangesModalOpen && (
            <CreateShopCancelSavedChanges
              closeModal={closeUnsavedChangesModalModal}
              exitWithoutSaveHandler={handleExit}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateShopBanner;
